import React, { useContext } from 'react'
import styled, { ThemeContext } from 'styled-components'
import { colors, colorConstants, paddingSizes, marginSizes } from '@jarvis.oddle/core/theme/default'
import { createArray } from 'src/utils/array'
import Flex from '@jarvis.oddle/core/components/Flex'
import Modal, { PrevButton, CloseButton } from '@jarvis.oddle/core/components/Modal'
import { H4 } from '@jarvis.oddle/core/components/Typo'
import Form, { FormContext } from '@jarvis.oddle/core/components/Form'
import DivButton from '@jarvis.oddle/core/components/DivButton'
import IconCalendar from '@jarvis.oddle/core/components/Icons/IconCalendar'
import { InlineNotification, notificationStates } from '@jarvis.oddle/core/components/InlineNotification'
import { isObjectEmpty } from 'src/utils/object'
import {
  getTranslatedWeekDays,
  getTranslatedMonths,
  parseDateString,
  isToday,
  add,
  formatLabelTimeSlot
} from 'src/utils/date'
import { Address } from 'src/components/molecules/CollectionDetails'
import pickUpDeliveryFlow from 'src/flows/pickUpDeliveryFlow'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import { fetchTimeSlotsAction, syncAppointmentTimeAction } from 'src/redux/order/action'
import useAppDispatch from 'src/hooks/useAppDispatcher'
import { fetchMenu } from 'src/redux/menu'
import useAppSelector from 'src/hooks/useAppSelector'
import { selectOrder } from 'src/redux/order'
import modalController, { makeControlledModal } from 'src/decorators/ModalController'

const DatePickerContainer = styled(Flex).attrs(() => ({
  alignSelf: 'stretch',
  justifyContent: 'center',
  marginBottom: marginSizes.medium,
  flexShrink: '0'
}))`
  ${props => props.withoutBorderAndBackground ? '' : `
    border: 1px ${colors.separator} solid;
    background-color: ${colorConstants.white};
  `}
`

const getNextFiveDays = (startDate) => createArray(5).map(index => {
  const date = new Date(startDate)
  date.setDate(date.getDate() + index)
  return date
})

const DateTimeSelectionModal = React.memo(({
  modal
}: {
  modal: {
    show: boolean
    onClose: () => void
    onClosed: () => void
  }
}) => {
  const formRef = React.useRef()
  const [showSimpleDatePicker, setShowSimpleDatePicker] = React.useState(true)
  const [nextFiveDays, setNextFiveDays] = React.useState([])
  const theme = useContext(ThemeContext)
  const { t } = useContext(TranslationContext)
  const dispatch = useAppDispatch()
  const maxDaysPickerConfig = useAppSelector((state) => state['@meta'].detailMenuSite.data?.maxDaysPickerConfig)
  const storeOrder = useAppSelector(selectOrder).storeOrder
  const availableTimeSlots = useAppSelector(selectOrder).availableTimeSlots

  /**
 * to support Oddle Pay implementation
 * https://oddle-pm.atlassian.net/wiki/spaces/OPM/pages/1321205863/Oddle+Pay+Phase+1
 * (story point number 6), only allow customers to order max 3 months in advance
 **/
  const disabledDays = {
    before: new Date(),
    after: add(new Date(), maxDaysPickerConfig, 'day').toDate()
  }
  const _getNextFiveDays = React.useCallback((formSelectedDate = null) => {
    const { collectionDate, earliestCollectionTime } = storeOrder.data

    const selectedDate = formSelectedDate || collectionDate || undefined
    // WORKAROUND: Date string return from backend don't have the same format
    // Backend improvement is needed. Track here https://oddle-pm.atlassian.net/browse/OMS-2654
    const earliestCollectionDate = parseDateString(earliestCollectionTime, 'DD MM YYYY')
    const startDate = selectedDate || earliestCollectionDate

    return getNextFiveDays(startDate).map((date) => {
      const allMonthsInOneYear = getTranslatedMonths(t, 'short')
      const translatedDaysOfWeek = getTranslatedWeekDays(t)
      return {
        day: date,
        dayOfWeek: isToday(date) ? t('Today') : translatedDaysOfWeek[date.getDay()],
        dayOfMonth: date.getDate(),
        month: allMonthsInOneYear[date.getMonth()]
      }
    })
  }, [storeOrder.data, t])

  const onSelectedDateChange = async ({ value: selectedDate, setFieldValue, setFieldTouched, shouldValidateOnChange }) => {
    setFieldValue('date', selectedDate, shouldValidateOnChange)
    await dispatch(fetchTimeSlotsAction({ date: selectedDate }))
    setFieldValue('slot', null, false)
    setFieldTouched('slot', false, false)
  }

  const validate = ({ slot }) => {
    if (!slot) {
      return {
        slot: t('Please select a time slot')
      }
    }
  }

  const handleSubmit = async ({ date, slot }, { setSubmitting, setFieldError }) => {
    const selectedTimeSlot = slot || availableTimeSlots.data[0]
    setSubmitting(true)
    const syncAppointmentTime = await dispatch(syncAppointmentTimeAction({
      collectionDate: date,
      collectionTimeSlot: selectedTimeSlot.y,
      collectionTimeSlotId: selectedTimeSlot.x
    }))
    if (syncAppointmentTime.meta.requestStatus === 'fulfilled') {
      dispatch(fetchMenu({}))
      pickUpDeliveryFlow.updateMeta({ isVisited: true })
      if (!pickUpDeliveryFlow.getMeta().onFinished) {
        modalController.showModal('CartDrawer')
      }
      pickUpDeliveryFlow.finish()
    } else {
      setFieldError('general', JSON.stringify(syncAppointmentTime.payload.payload.error))
    }
    setSubmitting(false)
  }

  const resetStateAndLoadData = async () => {
    // Reset state to show simple date picker, reset next five days
    setShowSimpleDatePicker(true)
    setNextFiveDays(_getNextFiveDays())

    const response = await dispatch(fetchTimeSlotsAction({ date: storeOrder.data.collectionDate }))
    if (response.meta.requestStatus === 'fulfilled') {
      const { payload } = response
      setInitialSelectedTimeSlot(payload.timeIntervals)
    }
  }

  const toggleDatePickerView = (formSelectedDate) => {
    setShowSimpleDatePicker((state) => !state)
    setNextFiveDays(_getNextFiveDays(formSelectedDate))
  }

  // NOTE: Due to temporary changes in product requirements, we follow V3 bahaviour to not disabling any days.
  // https://oddle-pm.atlassian.net/wiki/spaces/OPM/pages/647069751/Delivery+Store+Selection+Improvement
  // getDisabledDays = () => {
  //   const { availableStores, storeOrder } = props
  //   const { storeId, isPickup } = storeOrder.data

  //   const selectedStore = availableStores.data.find(s => s.id === storeId)
  //   const storeHours = selectedStore ? selectedStore.storeHours.filter(store => store.isPickup === isPickup) : []
  //   const daysOfWeeks = getDisabledDaysOfWeek(storeHours)
  //   const blockedOffDates = getBlockOffDates(storeHours)
  //   const disabledDays = [{ before: new Date() }, daysOfWeeks, ...blockedOffDates]

  //   return disabledDays
  // }

  // WORKAROUND: Set initial time slot when available timeslots is fetched
  const setInitialSelectedTimeSlot = (timeSlots) => {
    if (!formRef.current || (isObjectEmpty(timeSlots) || timeSlots.length < 1)) return

    const { setFieldValue } = formRef.current as {
      setFieldValue: (x, y) => void
    }
    const { collectionTime } = storeOrder.data
    const selectedTimeSlot = collectionTime && timeSlots.find((s) => s.y === collectionTime)
    setFieldValue('slot', selectedTimeSlot)
  }

  const { isPickup, collectionDate } = storeOrder.data
  const timeSlots = availableTimeSlots.status === 'fulfilled' ? availableTimeSlots.data : []
  const selectedDate = collectionDate || undefined
  // const firstAvailableTimeSlot = timeSlots.length > 0 ? timeSlots[0] : {}
  const noTimeSlots = availableTimeSlots.status === 'fulfilled' && availableTimeSlots.data.length === 0

  return (
    <Modal {...modal} onEnter={resetStateAndLoadData} slideInAnimation='bottom'>
      <Form initialValues={{ date: new Date(selectedDate), slot: undefined }} onValidate={validate} onSubmit={handleSubmit}>
        <Form.Ref ref={formRef} />
        <Modal.FlexContainer flexDirection='column'>
          <Modal.Header>
            <PrevButton onClick={() => pickUpDeliveryFlow.prev({ isPickup })} />
            <H4>{isPickup ? t('Pickup') : t('Delivery')}</H4>
            <CloseButton onClick={modal.onClose} />
          </Modal.Header>
          <Modal.Body>
            <Modal.Section>
              <Address
                storeOrder={storeOrder}
                onClick={() => pickUpDeliveryFlow.prev({ isPickup })} />
            </Modal.Section>
            <Modal.Section>
              <Flex justifyContent='space-between'>
                <H4 normalised marginBottom={marginSizes.small} bold>{t('Select a _appointmentType_ Date', { appointmentType: isPickup ? t('Pickup') : t('Delivery') })}</H4>
                <FormContext.Consumer>
                  {({ values }) => (
                    <DivButton onClick={() => toggleDatePickerView(values.date)}>
                      <Flex>
                        <IconCalendar fill={theme.buttonColor} />
                        <Flex paddingLeft={paddingSizes.extraSmall} alignItems='center'>
                          <H4 normalised bold color={theme.buttonColor}>{showSimpleDatePicker ? t('More Dates') : t('Less Dates')}</H4>
                        </Flex>
                      </Flex>
                    </DivButton>
                  )}
                </FormContext.Consumer>
              </Flex>
              <DatePickerContainer withoutBorderAndBackground={showSimpleDatePicker}>
                {showSimpleDatePicker
                  ? (
                    <Form.SimpleDatePicker
                      name='date'
                      resetOnUnmount={false}
                      fieldValidateOnChange={false}
                      onChange={onSelectedDateChange}
                      disabledDays={disabledDays}
                      days={nextFiveDays}
                    />
                  )
                  : (
                    <Form.DatePicker
                      name='date'
                      withMonthYearDropdown
                      resetOnUnmount={false}
                      fieldValidateOnChange={false}
                      disabledDays={disabledDays}
                      onChange={onSelectedDateChange}
                    />
                  )}
              </DatePickerContainer>
              {availableTimeSlots.data.length > 0 && (
                <React.Fragment>
                  <H4 bold normalised marginBottom={marginSizes.small}>{t('Select a _appointmentType_ Time', { appointmentType: isPickup ? t('Pickup') : t('Delivery') })}</H4>
                  <Form.Select
                    name='slot'
                    options={[{ y: t('Please select a timeslot'), disabled: true }].concat(timeSlots as undefined)}
                    labelFormatter={(option) => formatLabelTimeSlot(t, option?.y)}
                    renderSelected={(option) => formatLabelTimeSlot(t, option?.y || t('Please select a timeslot'))}
                    valueFormatter={(option) => option?.x && option?.y ? `${option.x}${option.y}` : undefined} />
                </React.Fragment>
              )}
              {noTimeSlots && (
                <InlineNotification
                  lightTheme
                  state={notificationStates.informative}
                  text={t('No available time for this day, please choose another day.')} />
              )}
            </Modal.Section>
            <Form.ErrorMessage>
              {({ errors }) => errors.general ? t(errors.general) : null}
            </Form.ErrorMessage>
          </Modal.Body>
          <Modal.Footer>
            <Form.SubmitButton disabled={noTimeSlots} fullWidth>{t('Next')}</Form.SubmitButton>
          </Modal.Footer>
        </Modal.FlexContainer>
      </Form>
    </Modal>
  )
})

export default makeControlledModal(DateTimeSelectionModal, 'DateTimeSelectionModal')
